import { logger } from '@gik/analytics/utils/logger';
import { dotnetApi } from '@gik/core/api/ky/dotnetApi';

function getPath(forId: string, isChildReaction: boolean) {
  return isChildReaction ? `stream/reactions/${forId}/children` : `stream/activities/${forId}/reactions`;
}

export type createReactionResponseBody = {
  id: string;
};

export async function createComment(forId: string, message: string, isChildReaction = false): Promise<string> {
  let reactionId: string;
  try {
    const response = await dotnetApi.post(getPath(forId, isChildReaction), {
      json: {
        type: 'comment',
        message,
      },
    });

    reactionId = response.headers.get('Location') || (await response.json<{ id: string }>()).id;
    if (!reactionId) {
      throw new Error('Reaction id not returned in location header or response body');
    }

    await publishReaction(reactionId);
  } catch (e) {
    logger.error('Failed to create comment: ', e);
    return null;
  }

  return reactionId;
}

export async function createLike(forId: string, emoji: string, isChildReaction = false): Promise<string> {
  let reactionId: string;
  try {
    const response = await dotnetApi.post(getPath(forId, isChildReaction), {
      json: {
        type: 'like',
        emoji,
      },
    });
    // eslint-disable-next-line
    reactionId = response.headers.get('Location') || ((await response.json()) as any).id;
    if (!reactionId) {
      throw new Error('Reaction id not returned in location header or response body');
    }
  } catch (e) {
    logger.error('Failed to create like: ', e);
    return null;
  }

  return reactionId;
}

export async function deleteReaction(reactionId: string, inkindRouteId?: string): Promise<boolean> {
  try {
    const url = inkindRouteId
      ? `stream/inkinds/${inkindRouteId}/reactions/${reactionId}`
      : `stream/reactions/${reactionId}`;
    const response = await dotnetApi.delete(url);
    if (!response.ok) {
      throw new Error(`Failed to delete reaction: ${response.status} ${response.statusText}`);
    }
    return true;
  } catch (e) {
    logger.error('Failed to delete reaction: ', e);
    return false;
  }
}

export async function adminDeleteReaction(reactionId: string): Promise<boolean> {
  try {
    const response = await dotnetApi.delete(`admin/reactions/${reactionId}`);
    if (!response.ok) {
      throw new Error(`Failed to undelete reaction: ${response.status} ${response.statusText}`);
    }
    return true;
  } catch (e) {
    logger.error('Failed to delete reaction: ', e);
    return false;
  }
}

export async function adminUndeleteReaction(reactionId: string): Promise<boolean> {
  try {
    const response = await dotnetApi.post(`admin/reactions/${reactionId}/undelete`);
    if (!response.ok) {
      throw new Error(`Failed to undelete reaction: ${response.status} ${response.statusText}`);
    }
    return true;
  } catch (e) {
    logger.error('Failed to undelete reaction: ', e);
    return false;
  }
}

export async function updateComment(reactionId: string, message: string): Promise<boolean> {
  try {
    const response = await dotnetApi.patch(`stream/reactions/${reactionId}`, {
      json: {
        message,
      },
    });
    if (!response.ok) {
      throw new Error(`Failed to update reaction: ${response.status} ${response.statusText}`);
    }
    return true;
  } catch (e) {
    logger.error('Failed to update reaction: ', e);
    return false;
  }
}

export async function publishReaction(reactionId: string) {
  await dotnetApi.post(`stream/reactions/${reactionId}/publish`);
}
